import { useEffect, useMemo, useState } from 'react';
import {
  Anchor,
  BackgroundImage,
  Box,
  Button,
  Center,
  Divider,
  Group,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useAppColorScheme } from '../lib/colorScheme';
import { notifications } from '@mantine/notifications';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';

function readFromLocationState(state: unknown): string | null {
  if (!state || typeof state !== 'object') return null;
  const from = (state as { from?: unknown }).from;
  return typeof from === 'string' ? from : null;
}

export function LoginPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const { colorScheme } = useAppColorScheme();

  const from = useMemo(() => {
    return readFromLocationState(loc.state) ?? '/';
  }, [loc.state]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = 'Musical!Kultur Admin';
  }, []);

  async function onLogin() {
    setBusy(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (error) throw error;

      if (!data.session) {
        notifications.show({
          color: 'yellow',
          title: 'Login',
          message: 'Kein Session-Token erhalten. Bitte erneut versuchen.',
        });
        return;
      }

      try {
        localStorage.removeItem('mk-admin-locked');
      } catch (_) {}
      nav(from, { replace: true });
    } catch {
      // Keine rohen Error-Details ausgeben (verhindert Information-Leaking)
      notifications.show({
        color: 'red',
        title: 'Login fehlgeschlagen',
        message: 'Email oder Passwort ungültig. Bitte erneut versuchen.',
      });
    } finally {
      setBusy(false);
    }
  }

  async function onForgotPassword() {
    if (!email.trim()) {
      notifications.show({
        color: 'yellow',
        title: 'Passwort reset',
        message: 'Bitte zuerst Email eintragen.',
      });
      return;
    }

    setBusy(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        // Route through /auth/callback so the same setup works for recovery/invite/magic links.
        redirectTo: window.location.origin + '/auth/callback?next=/reset-password',
      });
      if (error) throw error;
      notifications.show({
        color: 'teal',
        title: 'Mail gesendet',
        message: 'Wenn der Account existiert, kommt gleich eine Reset-Mail.',
      });
    } catch {
      // Keine rohen Error-Details ausgeben
      notifications.show({
        color: 'red',
        title: 'Reset fehlgeschlagen',
        message: 'Der Reset-Link konnte nicht gesendet werden. Bitte später erneut versuchen.',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box mih="100vh" pos="relative">
      <BackgroundImage
        src="/brand/mk_stage_bg.jpg"
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.28,
          filter: 'saturate(0.95) contrast(1.05)',
        }}
      />
      <Box
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(1100px 620px at 18% 6%, rgba(69, 61, 181, 0.55), transparent 60%), radial-gradient(900px 520px at 88% 4%, rgba(233, 80, 149, 0.22), transparent 55%), linear-gradient(180deg, rgba(43, 38, 34, 0.85), rgba(49, 44, 40, 0.96))',
        }}
      />

      <Center mih="100vh" pos="relative">
        <Paper
          radius="lg"
          p="xl"
          w={420}
          shadow="xl"
          style={{
            backdropFilter: 'blur(10px)',
            background: 'var(--app-surface)',
            border: '1px solid var(--app-border)',
          }}
          className="app-fade-in"
        >
          <Stack gap="md">
            <Box>
              <Group justify="space-between" align="center">
                <img
                  src={colorScheme === 'dark' ? '/brand/mk_logo_white.png' : '/brand/mk_logo_black.png'}
                  alt="Musical!Kultur Daaden e.V."
                  style={{ height: 26, width: 'auto', opacity: 0.95 }}
                />
              </Group>

              <Title order={2} c="var(--color-text)">
                Musical!Kultur Admin
              </Title>
              <Text c="dimmed">
                Bitte mit deinem Konto anmelden.
              </Text>
            </Box>

            <Divider color="var(--color-border)" />

            <TextInput
              label="Email"
              placeholder="name@chor.de"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              withAsterisk
              autoComplete="email"
              styles={{
                label: { color: 'var(--color-text-muted)' },
              }}
            />

            <PasswordInput
              label="Passwort"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              withAsterisk
              autoComplete="current-password"
              styles={{
                label: { color: 'var(--color-text-muted)' },
              }}
            />

            <Group justify="space-between">
              <Anchor
                component="button"
                type="button"
                c="brand.2"
                onClick={onForgotPassword}
                disabled={busy}
                style={{ textAlign: 'left' }}
              >
                Passwort vergessen?
              </Anchor>
              <Button
                onClick={onLogin}
                loading={busy}
                disabled={!email.trim() || !password}
              >
                Einloggen
              </Button>
            </Group>

            <Text size="xs" c="dimmed">
              Probleme beim Login? Passwort-Reset nutzen oder Admin kontaktieren.
            </Text>
          </Stack>
        </Paper>
      </Center>
    </Box>
  );
}
